import {
  Center,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { getUserFullName } from '@duke-hq/lib';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { skipToken } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { AdminUserPermissionsTab } from './AdminUserDetailTabs/AdminUserPermissionsTab';
import { FiMoreHorizontal, FiTrash2 } from 'react-icons/fi';
import { X } from 'lucide-react';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { useAuthAdmin } from '../AuthProviderAdmin';
import { useCheckPermission } from '../hooks/useCheckPermission';
import { iconSize } from '@duke-hq/ui/constants/scalable';

function UserDetails() {
  const auth = useAuthAdmin();
  const navigate = useNavigate();
  const canManagePermissions = useCheckPermission('canManagePermissions');

  const { isOpen, onClose, onOpen } = useDisclosure();
  const { userId, tab } = useParams<{ userId: string; tab: string }>();

  const utils = trpcAdmin.useUtils();

  const { data: user, isLoading } = trpcAdmin.users.byId.useQuery(
    userId !== undefined ? userId : skipToken,
    { enabled: Boolean(userId) },
  );

  const deleteUser = trpcAdmin.users.delete.useMutation({
    onSuccess: () => {
      void utils.users.all.refetch();
    },
  });

  const tabs = useMemo(
    () => (canManagePermissions === true ? [{ value: 'permissions', label: t`Permissions` }] : []),
    [canManagePermissions],
  );

  const selectedTab = useMemo(() => {
    if (typeof tab !== 'string') {
      return 0;
    }

    const tabIdx = tabs.findIndex((tabObj) => tabObj.value === tab);
    return tabIdx !== -1 ? tabIdx : 0;
  }, [tab, tabs]);

  const handleTabsChange = useCallback(
    (index: number) => {
      if (typeof tabs[index] !== 'undefined') {
        navigate(`/users/${userId}/${tabs[index]?.value}`);
      }
    },
    [userId, tabs, navigate],
  );

  const handleDeleteUser = useCallback(async () => {
    if (user == null) {
      return;
    }

    await deleteUser.mutateAsync({ id: user.id });
    await utils.users.all.refetch();
    onClose();
    navigate({
      pathname: `/users`,
      search: location.search,
    });
  }, [user, deleteUser, onClose, navigate]);

  if (isLoading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (!user) {
    return (
      <Center h="100vh">
        <Text>
          <Trans>User not found</Trans>
        </Text>
      </Center>
    );
  }

  return (
    <>
      <ConfirmationDialog
        isOpen={isOpen}
        onClose={onClose}
        title={<Trans>Delete user</Trans>}
        body={
          <Trans>Are you sure you want to delete this user? This action cannot be undone.</Trans>
        }
        onConfirm={handleDeleteUser}
        confirmLabel={<Trans>Delete</Trans>}
        variant="destructive"
        isLoading={deleteUser.isPending}
      />
      <Flex flexDirection="column" w="100%" h="100%">
        <DrawerHeader
          borderBottomWidth="1px"
          borderBottomColor="border.default"
          p={{ base: 2, md: 4 }}
        >
          <HStack justify="space-between" align="center">
            <HStack align="center" spacing="3">
              <UserAvatar
                user={{
                  first_name: user.first_name,
                  last_name: user.last_name,
                  picture_url: user.picture_url,
                }}
                size="lg"
              />
              <Text fontWeight="bold">
                {getUserFullName({
                  first_name: user.first_name,
                  last_name: user.last_name,
                  picture_url: user.picture_url,
                })}
              </Text>
            </HStack>
            <HStack spacing="1">
              {auth.user && auth.user.id !== user.id ? (
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="Options"
                    icon={<FiMoreHorizontal />}
                    variant="ghost"
                    size="sm"
                  />
                  <MenuList>
                    <MenuItem icon={<FiTrash2 size={iconSize.md} />} onClick={onOpen}>
                      <Trans>Delete user</Trans>
                    </MenuItem>
                  </MenuList>
                </Menu>
              ) : null}
              <IconButton
                aria-label="Close"
                icon={<X size={iconSize.lg} />}
                variant="ghost"
                size="sm"
                onClick={() => navigate({ pathname: '/users', search: location.search })}
              />
            </HStack>
          </HStack>
        </DrawerHeader>

        <DrawerBody p="0" border="none" m="0" paddingInline="0 !important">
          <Tabs
            isLazy
            flexDirection="column"
            display="flex"
            w="100%"
            h="100%"
            index={selectedTab}
            onChange={handleTabsChange}
          >
            <TabList bg="bg.subtle">
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.value}
                  bg={selectedTab === index ? 'bg.surface' : ''}
                  _hover={{ bg: 'bg.muted' }}
                >
                  {tab.label}
                </Tab>
              ))}
            </TabList>

            <TabPanels width="100%" height="100%" overflow="auto">
              {tabs.map((tab) => (
                <TabPanel key={tab.value} width="100%" height="100%" overflow="scroll" p={0}>
                  {tab.value === 'permissions' && <AdminUserPermissionsTab user={user} />}
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </DrawerBody>
      </Flex>
    </>
  );
}

export default UserDetails;
